import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { catchError, mergeMap, Observable, throwError } from 'rxjs';
import { StorageKeysEnum } from '../enums/storage.keys.enum';
import { AuthService } from './auth.service';
import { AuthUtils } from './auth.utils';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    /**
     * Constructor
     */
    constructor(private _authService: AuthService) {
    }

    /**
     * Intercept
     *
     * @param req
     * @param next
     */
    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Clone the request object
        let newReq = req.clone();

        // Request
        //
        // If the access token didn't expire, add the Authorization header.
        // We won't add the Authorization header if the access token expired.
        // This will force the server to return a "401 Unauthorized" response
        // for the protected API routes which our response interceptor will
        // catch and delete the access token from the local storage while logging
        // the user out from the app.
        if (this._authService.accessToken && !AuthUtils.isTokenValid(this._authService.accessToken, this._authService.accessTokenExpirationTime)) {
            newReq = req.clone({
                headers: req.headers.set('Authorization', 'Bearer ' + this._authService.accessToken)
            });
        }

        // Response
        return next.handle(newReq).pipe(
            catchError((error) => {
                if (error.url.endsWith('refreshToken')) {
                    // Sign out
                    this._authService.logout(true);
                    return throwError(() => error);
                }
                // Catch "401 Unauthorized" responses
                if (error instanceof HttpErrorResponse && error.status === 401) {
                    if (error.url.endsWith('changeUserStatus')) {
                        return throwError(() => error);
                    }
                    if (this._authService.refreshTokenIsValid()) {
                        return this._authService.doRefreshToken().pipe(
                            catchError((err) => {
                                this._authService.logout(true);
                                return throwError(() => err);
                            }),
                            mergeMap(() => {
                                req = req.clone({
                                    setHeaders: {
                                        // eslint-disable-next-line @typescript-eslint/naming-convention
                                        Authorization: `Bearer ${localStorage.getItem(StorageKeysEnum.accessToken)}`
                                    }
                                });
                                return next.handle(req).pipe(
                                    catchError((err) => {
                                        if (err instanceof HttpErrorResponse && err.status === 401) {
                                            this._authService.logout(true);
                                        }
                                        return throwError(() => err);
                                    })
                                );
                            })
                        );
                    } else {
                        // Sign out
                        this._authService.logout(true);
                        return throwError(() => error);
                    }
                }

                return throwError(() => error);
            })
        );
    }
}
